<template>
  <div class="admin-container">
    <div class="admin-header">
      <h1>Users</h1>
    </div>
    <div class="border-container-unique37">
      <div class="admin-list-title">List of Users</div>
      <div>You can add, edit, and delete all of Users from this page.</div>

      <div class="admin-flex-container" ref="adminFlexContainer">
        <div class="admin-search-container">
          <input
            type="text"
            class="admin-search-input"
            placeholder="Search..."
            v-model="searchQuery"
            @input="handleSearchChange"
          />
          <img
            src="/images/maker-cart/union.svg"
            alt="Search"
            class="admin-search-icon"
          />
        </div>
        <BaseButton
          variant="secondary"
          class="mx-2 my-md-auto"
          @click="handleAction({ type: 'add' })"
        >
          <div class="d-flex align-center ga-1">
            <img
              src="/images/icon/plus-circle.svg"
              width="20"
              height="20"
              alt="download-icon"
            />
            <span class="admin-button-text">Add Users</span>
          </div>
        </BaseButton>
      </div>
      <div class="admin-list-tool-container">
        <button
          v-for="(button, index) in buttons"
          :key="index"
          :class="{ 'checkbox-button-clicked': button.clicked }"
          @click="toggleClicked(index)"
          class="checkbox-button"
        >
          {{ button.label }}
        </button>
      </div>

      <TableData
        :headers="headers"
        :items="tableItems"
        :loading="tableLoading"
        :total-pages="data?.data?.total_pages || 0"
        :current-page="currentPage"
        @update:current-page="changePage"
        :is-action="true"
        :is-edit-action="true"
        :is-delete-action="true"
        @action="handleAction"
        :use-api-sort="false"
        :customActions="[]"
        :show-data-info="true"
        :total-rows="data?.data?.total_rows || 0"
        :current-limit="filters.limit"
        variant="rounded"
      />
    </div>
  </div>

  <ConfirmationModal
    variant="danger"
    v-model="showModalDelete"
    title="Remove User"
    message="Are you sure want to remove this user?"
    confirm-text="Confirm"
    cancel-text="Back"
    :loading="isLoading"
    @confirm="remove(itemId)"
    @cancel="updateVisibilityModalDelete"
  />
</template>

<script setup>
/* eslint-disable */
import { onMounted, ref, computed, watch, inject } from "vue";
import { useRouter } from "vue-router";
import { debounce } from "lodash";
import UserAdminService from "@/services/userAdminService.js";
import DateFormatter from "@/utils/DateFormatter.js";
import TableData from "@/components/common/TableData.vue";
import ConfirmationModal from "@/components/common/ConfirmationModal.vue";
import BaseButton from "@/components/common/BaseButton.vue";
import { useUrlQuerySync } from "@/composeables/useUrlQuerySync";

const showModalDelete = ref(false);
const isLoading = ref(false);
const itemId = ref(null);
const tableLoading = ref(true);
const searchQuery = ref("");
const notify = inject("notify");

const data = ref({
  message: "",
  error: null,
  data: {
    page: 1,
    limit: 10,
    total_rows: 0,
    total_pages: 0,
    rows: [],
  },
});

const currentPage = ref(1);
const filters = ref({
  search: "",
  page: 1,
  limit: 10,
  status: "",
});

// Setup URL query sync with filters
useUrlQuerySync({
  filters,
  excludeDefaults: [{ page: 1 }, { limit: 10 }],
});

const buttons = ref([
  { label: "All", clicked: true, status: "" },
  { label: "Active User", clicked: false, status: "active" },
  { label: "Pending User", clicked: false, status: "pending" },
  { label: "Blocked User", clicked: false, status: "blocked" },
]); // Array untuk state button

onMounted(async () => {
  await getAll();
  window.addEventListener("resize", updateLayout);
  updateLayout();
});

const formatToUserTimezone = (date) => {
  return DateFormatter.formatToUserTimezone(date);
};

// Define table headers
const headers = ref([
  { text: "User Type", value: "user_group_name", sortable: true },
  { text: "School Name", value: "school_name", sortable: true },
  { text: "Email", value: "email", sortable: true },
  { text: "Name", value: "name", sortable: true },
  { text: "Country", value: "country", sortable: true },
  { text: "Date Added", value: "date_added", sortable: true },
  { text: "Last Login", value: "last_login", sortable: true },
]);

// Transform API rows to table items
const tableItems = computed(() => {
  return (data.value?.data?.rows || []).map((item) => ({
    id: item.id,
    user_group_name: item.user_group_name || "-",
    school_name: item.school_name || "-",
    email: item.email || "-",
    name: item.name || "-",
    country: item.country || "-",
    date_added: formatToUserTimezone(item.date_added) || "-",
    last_login: formatToUserTimezone(item.last_login) || "-",
  }));
});

const getAll = async () => {
  try {
    tableLoading.value = true;
    const param = { page: filters.value.page, search: filters.value.search };

    // Map status to API params
    if (filters.value.status === "active") {
      param.status = "true";
    } else if (filters.value.status === "pending") {
      param.status = "true";
      param.approved = "false";
    } else if (filters.value.status === "blocked") {
      param.status = "false";
      param.approved = "false";
    }

    const response = await UserAdminService.getAll(param);
    if (response.status === 200) {
      data.value = response.data;
    }
  } catch (error) {
    console.log(error.message);
  } finally {
    tableLoading.value = false;
  }
};

// Sync search query with filters.search
watch(
  () => filters.value.search,
  (newValue) => {
    searchQuery.value = newValue || "";
  }
);

// Debounced search handler
const handleSearchChange = debounce(() => {
  filters.value.search = searchQuery.value;
  filters.value.page = 1;
  currentPage.value = 1;
  getAll();
}, 300);

const changePage = (page) => {
  currentPage.value = page;
  filters.value.page = page;
  getAll();
};

const openModal = (id) => {
  itemId.value = id;
  showModalDelete.value = true;
};

const updateVisibilityModalDelete = () => {
  showModalDelete.value = !showModalDelete.value;
};

const remove = async (id) => {
  isLoading.value = true;
  try {
    await UserAdminService.remove(id);
    showModalDelete.value = false;
    await getAll();
    notify && notify.success("User deleted successfully", "Success");
  } catch (error) {
    console.error(error.message);
    notify && notify.error("Failed to delete user", "Error");
  } finally {
    isLoading.value = false;
  }
};

// Function to update layout based on window width
const updateLayout = () => {
  const container = document.querySelector(".admin-flex-container");
  const toolContainer = document.querySelector(".admin-list-tool-container");
  const parentContainer = document.querySelector(".border-container-unique37");
  const width = window.innerWidth;

  if (width <= 991) {
    // Memindahkan toolContainer di bawah admin-flex-container
    parentContainer.insertBefore(toolContainer, container.nextSibling);
  } else {
    // Memindahkan toolContainer kembali ke dalam admin-flex-container
    container.appendChild(toolContainer);
    container.insertBefore(toolContainer, container.children[1]);
  }
};

// Function untuk toggle clicked state
const toggleClicked = (index) => {
  buttons.value.forEach((button, idx) => {
    button.clicked = idx === index;
  });
  filters.value.status = buttons.value[index].status;
  filters.value.page = 1;
  currentPage.value = 1;
  getAll();
};

// Keep buttons clicked state in sync with filters.status (from URL or interactions)
watch(
  () => filters.value.status,
  (newStatus) => {
    buttons.value.forEach((button) => {
      button.clicked = button.status === newStatus;
    });
  },
  { immediate: true }
);

// Handle action events from TableData
const router = useRouter();
const handleAction = (action) => {
  if (action.type === "edit") {
    router.push({ name: "UsersEditPage", params: { id: action.item.id } });
  } else if (action.type === "delete") {
    openModal(action.item.id);
  } else if (action.type === "add") {
    router.push({ name: "UsersFormPage" });
  }
};
</script>
